<script setup lang="ts">
import { Mail, Phone, ShieldCheck, Trash2, UserRound } from 'lucide-vue-next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/editing/ConfirmDialog.vue';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { Switch } from '@/components/ui/switch';
import {
    CLIENT_PORTAL_ROLE_OPTIONS,
    clientPortalRoleLabel,
    normalizeClientPortalRole,
    resolveClientPortalPermissions,
    type ClientPortalRole,
} from '~/lib/clientPortalPermissions';
import type { ClientContact, Project } from '~/types/models';

const clientStore = useClientStore();

const props = withDefaults(defineProps<{
    open: boolean;
    contact?: ClientContact | null;
    projects?: Array<Pick<Project, 'id' | 'name'>>;
    canDelete?: boolean;
    deleting?: boolean;
}>(), {
    canDelete: false,
    deleting: false,
});

const emit = defineEmits<{
    (event: 'update:open', value: boolean): void;
    (event: 'save', contact: ClientContact): void;
    (event: 'delete', contact: ClientContact): void;
}>();

type ContactFormState = {
    id: string;
    name: string;
    email: string;
    phone: string;
    role: string;
    isPrimary: boolean;
    portalRole: ClientPortalRole;
    projectAccessMode: 'all' | 'selected';
    projectIds: string[];
};

const form = ref<ContactFormState>(createState());
const deleteConfirmationOpen = ref(false);

const isEditing = computed(() => Boolean(props.contact?.id));
const isPrimary = computed(() => form.value.isPrimary);
const availableProjects = computed(() => props.projects || []);
const resolvedRoleLabel = computed(() => clientPortalRoleLabel(form.value.portalRole, isPrimary.value));
const selectedProjectCount = computed(() => form.value.projectIds.length);

function createState(contact?: ClientContact | null): ContactFormState {
    const primary = contact?.isPrimary === true;
    const portalRole = normalizeClientPortalRole(contact?.portalRole, primary);
    const projectAccessMode = primary
        || portalRole === 'client_owner'
        ? 'all'
        : contact?.projectAccessMode === 'all'
            ? 'all'
            : 'selected';

    return {
        id: contact?.id || clientStore.createClientContactId(),
        name: contact?.name || '',
        email: contact?.email || '',
        phone: contact?.phone || '',
        role: contact?.role || '',
        isPrimary: primary,
        portalRole,
        projectAccessMode,
        projectIds: projectAccessMode === 'selected' && Array.isArray(contact?.projectIds)
            ? [...contact.projectIds]
            : [],
    };
}

function resetForm() {
    form.value = createState(props.contact);
}

function closeDialog() {
    emit('update:open', false);
}

function toggleProject(projectId: string, value: boolean) {
    const next = new Set(form.value.projectIds);
    if (value) {
        next.add(projectId);
    } else {
        next.delete(projectId);
    }
    form.value.projectIds = Array.from(next);
}

function handlePortalRoleChange(value: string) {
    form.value.portalRole = normalizeClientPortalRole(value, isPrimary.value);
    if (form.value.portalRole === 'client_owner') {
        form.value.projectAccessMode = 'all';
        form.value.projectIds = [];
    }
}

function handleProjectCheckboxChange(projectId: string, event: Event) {
    toggleProject(projectId, (event.target as HTMLInputElement).checked);
}

function buildContactPayload(): ClientContact {
    const portalRole = normalizeClientPortalRole(form.value.portalRole, form.value.isPrimary);
    const projectAccessMode = form.value.isPrimary || portalRole === 'client_owner' || form.value.projectAccessMode === 'all'
        ? 'all'
        : 'selected';

    return {
        id: form.value.id,
        name: form.value.name.trim() || form.value.email.trim().split('@')[0] || 'Client contact',
        email: form.value.email.trim().toLowerCase(),
        ...(form.value.phone.trim() ? { phone: form.value.phone.trim() } : {}),
        ...(form.value.role.trim() ? { role: form.value.role.trim() } : {}),
        ...(form.value.isPrimary ? { isPrimary: true } : {}),
        projectAccessMode,
        projectIds: projectAccessMode === 'selected' ? form.value.projectIds : [],
        portalRole,
        portalPermissions: resolveClientPortalPermissions({
            isPrimary: form.value.isPrimary,
            portalRole,
        }),
    };
}

function handleSave() {
    emit('save', buildContactPayload());
}

function handleDelete() {
    if (!props.contact) return;
    emit('delete', props.contact);
}

watch(() => props.open, (open) => {
    if (open) {
        resetForm();
    } else {
        deleteConfirmationOpen.value = false;
    }
});

watch(() => props.contact, () => {
    if (props.open) {
        resetForm();
    }
}, { deep: true });
</script>

<template>
    <Sheet :open="open" @update:open="(value) => emit('update:open', value)">
        <SheetContent
            class="h-full !w-full !max-w-none gap-0 overflow-hidden p-0 sm:!max-w-2xl"
        >
            <SheetHeader class="border-b p-5 text-left sm:p-6">
                <div class="flex flex-col gap-4">
                    <div class="space-y-2">
                        <div class="space-y-2">
                            <SheetTitle class="text-2xl font-semibold tracking-tight">
                                {{ isEditing ? 'Edit contact' : 'Add contact' }}
                            </SheetTitle>
                            <SheetDescription class="max-w-2xl text-sm leading-6 text-muted-foreground">
                                Keep contact details, portal role, and default project access tidy without opening the full client editor.
                            </SheetDescription>
                        </div>
                    </div>
                    <div class="flex flex-wrap gap-2">
                        <Badge v-if="isPrimary" variant="outline">Primary</Badge>
                        <Badge variant="secondary">{{ resolvedRoleLabel }}</Badge>
                        <Badge variant="outline">
                            {{ form.projectAccessMode === 'all' ? 'All shared projects' : `${selectedProjectCount} selected project${selectedProjectCount === 1 ? '' : 's'}` }}
                        </Badge>
                    </div>
                </div>
            </SheetHeader>

            <div class="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
                <div class="space-y-8">
                    <section class="space-y-5">
                        <div class="mb-4 flex items-start gap-3">
                            <div class="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-border/70 bg-muted/40">
                                <UserRound class="size-5 text-muted-foreground" />
                            </div>
                            <div>
                                <p class="text-sm font-semibold text-foreground">Contact details</p>
                                <p class="text-sm leading-6 text-muted-foreground">The person your team will see across client and portal surfaces.</p>
                            </div>
                        </div>

                        <div class="space-y-4">
                            <div class="space-y-2">
                                <Label for="contact-dialog-name">Name</Label>
                                <Input id="contact-dialog-name" v-model="form.name" placeholder="Alexandra Sissons" />
                            </div>
                            <div class="space-y-2">
                                <Label for="contact-dialog-role">Client role</Label>
                                <Input id="contact-dialog-role" v-model="form.role" placeholder="Marketing lead" />
                            </div>
                            <div class="space-y-2">
                                <Label for="contact-dialog-email">Email</Label>
                                <div class="relative">
                                    <Mail class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input id="contact-dialog-email" v-model="form.email" type="email" class="pl-9" placeholder="alexandra@example.com" />
                                </div>
                            </div>
                            <div class="space-y-2">
                                <Label for="contact-dialog-phone">Phone</Label>
                                <div class="relative">
                                    <Phone class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input id="contact-dialog-phone" v-model="form.phone" class="pl-9" placeholder="+44 7700 000000" />
                                </div>
                            </div>
                        </div>
                    </section>

                    <section class="space-y-5 border-t border-border/60 pt-6">
                        <div class="flex items-start gap-3">
                            <div class="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-border/70 bg-muted/40">
                                <ShieldCheck class="size-5 text-muted-foreground" />
                            </div>
                            <div>
                                <p class="text-sm font-semibold text-foreground">Portal access defaults</p>
                                <p class="text-sm leading-6 text-muted-foreground">
                                    These defaults are used when inviting this contact and when syncing existing portal access.
                                </p>
                            </div>
                        </div>

                        <div class="space-y-5">
                            <div class="space-y-2">
                                <Label for="contact-dialog-portal-role">Portal role</Label>
                                <SearchableSelectMenu
                                    id="contact-dialog-portal-role"
                                    :model-value="form.portalRole"
                                    :options="CLIENT_PORTAL_ROLE_OPTIONS"
                                    :disabled="isPrimary"
                                    placeholder="Choose a portal role"
                                    search-placeholder="Search portal roles..."
                                    show-all-by-default
                                    variant="form"
                                    @update:model-value="handlePortalRoleChange(String($event || ''))"
                                />
                                <p class="text-xs leading-5 text-muted-foreground">
                                    {{ isPrimary ? 'Primary contacts always keep full portal access.' : CLIENT_PORTAL_ROLE_OPTIONS.find((role) => role.value === form.portalRole)?.description }}
                                </p>
                            </div>

                            <div class="space-y-2">
                                <Label>Project access</Label>
                                <div class="rounded-xl border border-border/70 bg-muted/20 p-3">
                                    <div class="flex items-center justify-between gap-3">
                                        <div>
                                            <p class="text-sm font-medium text-foreground">All shared projects</p>
                                            <p class="text-xs text-muted-foreground">
                                                {{ isPrimary ? 'Required for primary contacts.' : 'Use this for account owners or senior client leads.' }}
                                            </p>
                                        </div>
                                        <Switch
                                            :checked="form.projectAccessMode === 'all'"
                                            :disabled="isPrimary"
                                            @update:checked="(value) => {
                                                form.projectAccessMode = value ? 'all' : 'selected';
                                                if (value) form.projectIds = [];
                                            }"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="!isPrimary && form.projectAccessMode === 'selected'" class="space-y-3">
                            <div class="flex items-center justify-between gap-3">
                                <div>
                                    <p class="text-sm font-medium text-foreground">Selected projects</p>
                                    <p class="text-xs text-muted-foreground">Choose the projects this contact should see by default.</p>
                                </div>
                                <Badge variant="outline">{{ selectedProjectCount }} selected</Badge>
                            </div>

                            <div v-if="availableProjects.length === 0" class="rounded-xl border border-dashed border-border/70 bg-muted/20 p-3 text-sm text-muted-foreground">
                                No projects exist for this client yet. You can still save the contact and share projects later.
                            </div>

                            <div v-else class="max-h-72 overflow-y-auto rounded-xl border border-border/70 bg-background">
                                <label
                                    v-for="project in availableProjects"
                                    :key="project.id"
                                    class="flex cursor-pointer items-center justify-between gap-3 border-b border-border/60 px-3 py-3 text-sm transition-colors last:border-b-0 hover:bg-muted/30"
                                >
                                    <span class="min-w-0 truncate">{{ project.name || project.id }}</span>
                                    <input
                                        type="checkbox"
                                        class="size-4 rounded border-border"
                                        :checked="form.projectIds.includes(project.id)"
                                        @change="(event) => handleProjectCheckboxChange(project.id, event)"
                                    >
                                </label>
                            </div>
                        </div>
                    </section>
                    <section class="border-t border-border/60 pt-6 text-sm leading-6 text-muted-foreground">
                        Role changes sync into existing portal invitations and active access records after save.
                    </section>
                </div>
            </div>

            <SheetFooter class="flex-row items-center justify-between gap-2 border-t bg-background p-5 sm:justify-between sm:gap-2">
                <Button
                    v-if="isEditing && canDelete"
                    type="button"
                    variant="destructive"
                    :disabled="deleting"
                    @click="deleteConfirmationOpen = true"
                >
                    <Trash2 class="mr-2 size-4" />Delete contact
                </Button>
                <span v-else />
                <div class="flex items-center gap-2">
                    <Button type="button" variant="outline" :disabled="deleting" @click="closeDialog">Cancel</Button>
                    <Button type="button" :disabled="deleting" @click="handleSave">Save contact</Button>
                </div>
            </SheetFooter>
        </SheetContent>
    </Sheet>

    <ConfirmDialog
        v-model:open="deleteConfirmationOpen"
        title="Delete this contact?"
        :description="`This permanently removes ${contact?.name || 'this contact'} from the client and revokes any pending or active portal access for them. This cannot be undone.`"
        confirm-label="Delete contact"
        destructive
        :loading="deleting"
        @confirm="handleDelete"
    />
</template>
